<template>
  <div class="dynamic-component-element" :style="wrapperStyle">
    <iColumn
      v-if="editMode && missingContexts.length"
      gap="extraSmall"
      width="hug"
      :style-overrides="{paddingTop: 'extraSmall', paddingBottom: 'extraSmall', paddingLeft: 'small', paddingRight: 'small'}"
    >
      <iText
        v-for="missing in missingContexts"
        :key="missing.name"
        :data-test="`missing-context-warning-${missing.name}`"
        variant="warning"
        font-size="small"
      >
        Missing context: {{ missing.name }}{{ missing.entityType ? ` (${missing.entityType})` : "" }}
      </iText>
    </iColumn>
    <BrokenContextElement
      v-if="scopedContextIsBroken"
      :component-name="String(referencedComponentId)"
      :problems="scopedContextProblems"
    />
    <DynamicComponentProductionElement
      v-else-if="canRenderReference && scopedContextGatePasses"
      :dynamic-component-id="referencedComponentId"
      :element-id="referencedRootElementId"
      :theme-mode="themeMode"
      :is-production="renderingInProduction"
    />
    <div v-else-if="isCycle" class="placeholder placeholder-error">
      Component cycle detected
    </div>
  </div>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import { isCompiledRenderSurface, isBuilderAppBooted, contextualAppName }
  from "%/dynamic-component-editor/classes/platform-standard-entities";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicComponentProductionElement
  from "%/dynamic-component-editor/components/DynamicComponentProductionElement.vue";
import BrokenContextElement from "%/dynamic-component-editor/components/BrokenContextElement.vue";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import { hasCompiledElement, readCompiledOption }
  from "%/dynamic-components/render-engine/compiled-payload-store";
import ReferencedComponentId from "%/dynamic-component-editor/options/referenced-component-id";
import ScopedProvidesContextsMixin from "%/dynamic-component-editor/mixins/scoped-provides-contexts-mixin";
import VariableOverrides from "%/dynamic-component-editor/options/variable-overrides";
import ContextBindings from "%/dynamic-component-editor/options/context-bindings";
import { sourceNameRemaps, remapEntityBag }
  from "%/dynamic-component-editor/helpers/context-bindings";
import { boundSourceContext } from "%/dynamic-component-editor/helpers/bound-source-context";
import { Context } from "%/dynamic-component-editor/classes/context-v2";
import { entityScopeFromBindings, primitiveScopeFromBindings }
  from "%/dynamic-component-editor/helpers/component-binding-scope";
import { resolveContextEngineAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MinHeight from "%/dynamic-component-editor/options/min-height";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import { embeddedComponentId } from "%/dynamic-component-editor/helpers/embedded-component-id";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import {
  readContextBinding,
  resolveStoryIdForContext,
  isStoryTypedBinding,
  passesExclusivePresenceGate,
} from "%/dynamic-component-editor/standard-components/base-story-card/resolve-bound-story";
import { readRequiredContexts, readProvidesContexts } from "@studio/Helpers/studio-installed-apps-bootstrap";
import { passesPublishGate } from "%/dynamic-component-editor/standard-components/base-story-card/story-publish-gate";
import SubscriberGate from "%/dynamic-component-editor/options/subscriber-gate";
import { isViewerSubscribed } from "%/dynamic-component-editor/standard-elements/upsell-element/resolve-viewer-subscription";
import { useCollaboratorStore } from "~/stores/collaborator-store";

function isMeaningfulOverride(value) {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (typeof value !== "object") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some(item => item !== null && item !== undefined && item !== "");
  }
  return Object.values(value).some(v => v !== null && v !== undefined && v !== "");
}

export default {
  name: "DynamicComponentElement",
  components: { DynamicComponentProductionElement, BrokenContextElement },
  mixins: [DynamicElementMixin, ScopedProvidesContextsMixin],
  elementPanelSettings: {
    displayName: "Component",
    displayIcon: "box",
    elementType: "element",
    elementGroup: "reusable",
  },
  options: [ReferencedComponentId, VariableOverrides, ContextBindings, SubscriberGate],
  optionBundles: [
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    new LayoutWidthHeightOptions({
      "default.layout-width": "",
      "default.layout-height": "",
    }),
  ],
  inject: {
    ancestorDynamicComponentIds: {
      from: "ancestorDynamicComponentIds",
      default: () => [],
    },
    inheritedUseExampleData: {
      from: "useExampleData",
      default: () => () => false,
    },
    parentPageEntities: {
      from: "pageEntities",
      default: null,
    },
    renderingInProduction: {
      from: "renderingInProduction",
      default: false,
    },
    inBuilderEditScope: {
      from: "inBuilderEditScope",
      default: false,
    },
  },
  provide() {
    return {
      useExampleData: () => {
        if (this.editMode || this.inBuilderEditScope) {
          return true;
        }
        return typeof this.inheritedUseExampleData === "function" && Boolean(this.inheritedUseExampleData());
      },
      pageEntities: () => this.remappedPageEntities(),
      ancestorDynamicComponentIds: [
        ...this.ancestorDynamicComponentIds,
        this.dynamicComponentInstance?.id,
      ].filter(id => id !== undefined && id !== null),
      variableOverridesForCurrentDC: this.resolveVariableOverridesForState,
    };
  },
  data() {
    return {
      ReferencedComponentId,
      VariableOverrides,
      referencedRootElementIdTick: 0,
    };
  },
  computed: {
    referencedComponentId() {
      return embeddedComponentId(this.getOptionValue(ReferencedComponentId));
    },
    isCycle() {
      if (!this.referencedComponentId) {
        return false;
      }
      return this.ancestorDynamicComponentIds
          .map(String)
          .includes(String(this.referencedComponentId));
    },
    referencedComponent() {
      if (!this.referencedComponentId || this.isCycle) {
        return null;
      }
      return DynamicComponent.get(this.referencedComponentId, this.renderingInProduction);
    },
    referencedRootElementId() {
      const id = this.referencedComponentId;
      if (!id || this.isCycle) {
        return null;
      }
      if (this.renderingInProduction || isCompiledRenderSurface() || !isBuilderAppBooted()) {
        // eslint-disable-next-line no-unused-expressions
        this.referencedRootElementIdTick;
        return this.referencedComponent?.rootElementId ?? null;
      }
      const entity = App.get(contextualAppName()).getEntity("DynamicComponent", { id: String(id) });
      return entity?.rootElement?.pk?.id ?? null;
    },
    storyGateBinding() {
      return readContextBinding(this.optionValues, this.configuration);
    },
    isStoryGatedEmbed() {
      const binding = this.storyGateBinding;
      return !!binding.name && isStoryTypedBinding(binding.entityType);
    },
    gatedStoryId() {
      if (!this.isStoryGatedEmbed) {
        return 0;
      }
      return resolveStoryIdForContext(this.storyGateBinding.name, {
        scopedProvidedContexts: this.inheritedScopedProvidedContexts,
        pageEntities: this.parentPageEntities,
        entityId: this.entityId,
      });
    },
    gatedStory() {
      const id = this.gatedStoryId;
      return id ? useStoryStore().stories?.[id] ?? null : null;
    },
    isGatePreviewMode() {
      return typeof this.inheritedUseExampleData === "function" && Boolean(this.inheritedUseExampleData());
    },
    passesExclusivePresenceGate() {
      return passesExclusivePresenceGate({
        isStoryGatedEmbed: this.isStoryGatedEmbed,
        editMode: this.editMode,
        isPreviewMode: this.isGatePreviewMode,
        story: this.gatedStory,
      });
    },
    viewerCanEditPublication() {
      try {
        return Boolean(useCollaboratorStore().canEditPublication);
      } catch {
        return false;
      }
    },
    passesSubscriberPresenceGate() {
      const value = this.getOptionValue(SubscriberGate);
      const isSubscriberGated = value === "yes" || value === true;
      if (!isSubscriberGated) {
        return true;
      }
      if (this.editMode || this.isGatePreviewMode) {
        return true;
      }
      if (this.viewerCanEditPublication) {
        return true;
      }
      return !isViewerSubscribed();
    },
    canRenderReference() {
      return !!this.referencedComponentId
          && !this.isCycle
          && !!this.referencedRootElementId
          && this.passesExclusivePresenceGate
          && this.passesSubscriberPresenceGate
          && this.passesStoryPublishGate;
    },
    constantBoundStoryIds() {
      const bindings = this.contextBindingsMap;
      const scoped = this.extraScopedEntityContexts();
      const storyIdOf = entry => {
        if (entry?.entitySource !== "constant" || !isStoryTypedBinding(entry.entityType)) {
          return 0;
        }
        return Number(entry.entityPkUrlVars?.id ?? 0);
      };
      const required = this.referencedComponent
        ? Object.keys(readRequiredContexts(this.referencedComponent) ?? {})
        : [];
      if (!required.length) {
        const all = Object.values(scoped).map(storyIdOf).filter(id => id > 0);
        if (all.length) {
          const gateMessage = `DynamicComponentElement ${this.identifier}: component `
            + `${this.referencedComponentId} exposes no readable required-contexts here, so every `
            + "constant Story binding on this embed is publish-gated. If that hides a component it "
            + "should not, the component's required-contexts did not survive into this payload.";
          console.warn(gateMessage); // eslint-disable-line no-console
        }
        return all;
      }
      const ids = [];
      required.forEach(name => {
        const remap = typeof bindings?.[name] === "string" ? bindings[name] : null;
        const id = storyIdOf(scoped[name] ?? (remap ? scoped[remap] : null));
        if (id > 0) {
          ids.push(id);
        }
      });
      return ids;
    },
    passesStoryPublishGate() {
      const ids = this.constantBoundStoryIds;
      if (!ids.length) {
        return true;
      }
      return ids.every(id => passesPublishGate({
        onRenderSurface: isCompiledRenderSurface() && !this.editMode && !this.isGatePreviewMode,
        story: this.publishStateForStory(id),
      }));
    },
    missingContexts() {
      if (!this.referencedComponent) {
        return [];
      }
      const required = readRequiredContexts(this.referencedComponent);
      if (!Object.keys(required).length) {
        return [];
      }
      return useDynamicComponentEditorStore()
          .embedUnsatisfiedContexts(required, this.getOptionValue(ContextBindings));
    },
    variableOverridesMap() {
      const raw = this.getOptionValue(VariableOverrides);
      if (!raw || typeof raw !== "object") {
        return {};
      }
      return raw;
    },
    contextBindingsMap() {
      return sourceNameRemaps(this.getOptionValue(ContextBindings));
    },
    contextBindingObjects() {
      const raw = this.getOptionValue(ContextBindings);
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return {};
      }
      return Object.fromEntries(Object.entries(raw)
          .filter(([, binding]) => binding && typeof binding === "object" && !Array.isArray(binding)));
    },
    referencedRootElement() {
      const rootId = this.referencedRootElementId;
      if (!rootId) {
        return null;
      }
      return DynamicElement.get(String(rootId));
    },
    referencedDynamicComponent() {
      return this.referencedComponent ?? null;
    },
    wrapperStyle() {
      const style = {};
      const rootElement = this.referencedRootElement;
      if (rootElement) {
        const width = this.resolveRootElementOptionValue(rootElement, "layout-width");
        const height = this.resolveRootElementOptionValue(rootElement, "layout-height");
        if (width !== undefined && width !== null && width !== "") {
          style.width = LayoutWidth.getCssValue(width);
        }
        if (height !== undefined && height !== null && height !== "") {
          style.height = LayoutHeight.getCssValue(height);
        }
      }
      const ownWidth = this.getOptionValue(LayoutWidth);
      const ownHeight = this.getOptionValue(LayoutHeight);
      if (ownWidth) {
        style.width = LayoutWidth.getCssValue(ownWidth);
      }
      if (ownHeight) {
        style.height = LayoutHeight.getCssValue(ownHeight);
      }
      [
        ["maxWidth", MaxWidth],
        ["maxHeight", MaxHeight],
        ["minWidth", MinWidth],
        ["minHeight", MinHeight],
      ].forEach(([cssProperty, optionClass]) => {
        const value = this.getOptionValue(optionClass);
        if (!value || value === "unset") {
          return;
        }
        style[cssProperty] = typeof optionClass.getCssValue === "function"
          ? optionClass.getCssValue(value)
          : value;
      });
      return Object.keys(style).length ? style : null;
    },
  },
  watch: {
    "referencedComponent.rootElementId": {
      handler() {
        this.referencedRootElementIdTick += 1;
        // Staging renders with real pks too; only the builder canvas (pk-less contexts) skips resolving.
        if (!this.editMode && this.referencedComponent) {
          this.resolveScopedProvidedContexts(this.referencedComponent, String(this.referencedComponentId));
        }
      },
      immediate: true,
    },
    gatedStoryId: {
      handler(id) {
        if (!this.isStoryGatedEmbed) {
          return;
        }
        if (id) {
          useStoryStore().getOrFetchStory(id);
          return;
        }
        if (this.editMode || this.isGatePreviewMode) {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn(
          "[exclusive-badge] story-gated embed could not resolve its bound row story — the lock stays hidden",
          {
            identifier: this.identifier,
            boundContext: this.storyGateBinding.name,
            availableScopeKeys: Object.keys(this.inheritedScopedProvidedContexts?.() ?? {}),
          },
        );
      },
      immediate: true,
    },
  },
  methods: {
    publishStateForStory(id) {
      try {
        const isPublished = App.get(contextualAppName())
            .getEntityField("Story", { id: String(id) }, "isPublished");
        return isPublished === undefined || isPublished === null ? null : { isPublished };
      } catch {
        return null;
      }
    },
    scopedContextComponentDefs() {
      if (!this.referencedComponent) {
        return null;
      }
      return {
        componentName: this.referencedComponent.name ?? String(this.referencedComponentId),
        required: readRequiredContexts(this.referencedComponent),
        provides: readProvidesContexts(this.referencedComponent),
      };
    },
    remappedPageEntities() {
      const parent = typeof this.parentPageEntities === "function"
        ? this.parentPageEntities() ?? {}
        : this.parentPageEntities ?? {};
      return remapEntityBag(parent, this.contextBindingsMap);
    },
    lookupExtraScopedContext(name) {
      const sourceName = this.contextBindingsMap?.[name];
      if (typeof sourceName !== "string" || !sourceName) {
        return null;
      }
      return boundSourceContext(sourceName, {
        inherited: this.inheritedScopedProvidedContexts?.(),
        lookupGlobal: name => Context.getContext(name),
      });
    },
    extraScopedPrimitiveContexts() {
      const bindings = this.contextBindingObjects;
      if (!Object.keys(bindings).length) {
        return {};
      }
      return primitiveScopeFromBindings({
        bindings,
        contexts: {
          ...Context.allContexts,
          ...this.builderPrimitiveValueContexts(),
          ...Context.allValueContexts,
          ...this.inheritedScopedProvidedContexts?.() ?? {},
          ...this.scopedContexts,
        },
        readEntityField: (entityType, pk, fieldName, appName) => {
          const app = App.get(resolveContextEngineAppName(appName));
          const bareType = String(entityType ?? "").split(".").at(-1);
          return app?.getEntityField(bareType, pk, fieldName);
        },
      });
    },
    extraScopedEntityContexts() {
      const storeContextItems = (() => {
        try {
          return useDynamicComponentEditorStore(this.$pinia).contextItems ?? {};
        } catch {
          return {};
        }
      })();
      // Raw bindings: name remaps ({ thisChannel: "chatRoom_currentChannel" }) bind entities too, not just objects.
      return entityScopeFromBindings({
        bindings: this.getOptionValue(ContextBindings),
        required: this.referencedComponent ? readRequiredContexts(this.referencedComponent) : {},
        lookupSourceContext: name => boundSourceContext(name, {
          inherited: this.inheritedScopedProvidedContexts?.(),
          lookupGlobal: globalName => Context.getContext(globalName) ?? storeContextItems[globalName],
        }),
      });
    },
    resolveRootElementOptionValue(rootElement, optionKey) {
      const referencedDc = this.referencedDynamicComponent;
      if (referencedDc) {
        const variableId = referencedDc.getVariableBindingForOption?.(rootElement.id, optionKey);
        if (variableId !== null && variableId !== undefined) {
          const override = this.variableOverridesMap?.[variableId];
          if (override !== undefined && override !== null && override !== "") {
            return override;
          }
          const variableDefault = referencedDc.getVariableValue?.(variableId);
          if (variableDefault !== undefined && variableDefault !== null && variableDefault !== "") {
            return variableDefault;
          }
        }
      }
      if (hasCompiledElement(rootElement.id)) {
        return readCompiledOption(rootElement.id, optionKey, this.themeMode, "default", "default");
      }
      const options = rootElement.parsedOptions;
      if (!options) {
        return undefined;
      }
      const mode = this.themeMode;
      const leaf = options[mode]?.default?.default?.[optionKey]
        ?? options["light-mode"]?.default?.default?.[optionKey];
      return leaf?.r;
    },
    resolveVariableOverridesForState(breakpoint = "default", state = "default") {
      if (!this.dynamicElement) {
        return {};
      }
      const slotMap = this.dynamicElement.getRawOptionValue("variable-overrides", breakpoint, state)?.r;
      const defaultMap = breakpoint === "default" && state === "default"
        ? null
        : this.dynamicElement.getRawOptionValue("variable-overrides", "default", "default")?.r;
      const out = {};
      const copyMeaningful = source => {
        if (!source || typeof source !== "object") {
          return;
        }
        for (const [variableId, value] of Object.entries(source)) {
          if (isMeaningfulOverride(value)) {
            out[variableId] = value;
          }
        }
      };
      copyMeaningful(defaultMap);
      copyMeaningful(slotMap);
      return out;
    },
  },
};
</script>

<style lang="scss" scoped>
.dynamic-component-element {
  min-width: 0;
}

.dynamic-component-element:not(.element-hidden) {
  display: flex;
  flex-direction: column;
}

.placeholder-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 12px 16px;
  border: 1px dashed #dc2626;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
  background-color: rgba(220, 38, 38, 0.06);
}
</style>
